import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Hook that reports whether the referenced element's text is truncated
 * (i.e. clipped by `text-overflow: ellipsis` / `overflow: hidden`).
 *
 * Recomputes on mount/remount (via a callback ref), whenever `dependency`
 * changes, and when the element is resized (via ResizeObserver), so it stays
 * accurate as the layout changes — including when the measured element is
 * conditionally rendered (e.g. toggled in/out of the DOM by inline editing).
 *
 * @param {*} dependency - Value that should re-trigger the check when it changes (e.g. the text).
 * @returns {[function, boolean]} - Callback ref to attach to the element and whether it is truncated.
 */
const useIsTruncated = (dependency) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const elRef = useRef(null);
    const observerRef = useRef(null);

    const measure = useCallback(() => {
        const el = elRef.current;
        if (el) {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        }
    }, []);

    // Callback ref: runs whenever the element mounts/unmounts, re-measuring and
    // (re)attaching the ResizeObserver each time. This keeps the result correct
    // when the element is removed and re-added to the DOM (e.g. leaving edit mode).
    const ref = useCallback(
        (node) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            elRef.current = node;
            if (!node) {
                return;
            }
            measure();
            if (typeof ResizeObserver === "undefined") {
                return;
            }
            observerRef.current = new ResizeObserver(measure);
            observerRef.current.observe(node);
        },
        [measure],
    );

    // Re-measure when the text (dependency) changes while the element stays mounted.
    useLayoutEffect(() => {
        measure();
    }, [dependency, measure]);

    return [ref, isTruncated];
};

export default useIsTruncated;
